// Merchant rules confirmed October 7, 2026. UPS calendar provenance is recorded in
// products/submariner-16613-delivery-sources.json. Extend verifiedThrough only after
// checking the next official calendar; never infer future UPS holiday operations.
export const shippingPolicy = Object.freeze({
  timeZone: 'America/Los_Angeles',
  cutoffHour: 15,
  dispatchWeekdays: Object.freeze([2, 3, 4, 5]),
  verifiedFrom: '2026-01-01',
  verifiedThrough: '2026-12-31',
  closed: Object.freeze(['2026-01-01', '2026-01-19', '2026-05-25', '2026-07-04', '2026-09-07', '2026-11-26', '2026-12-25']),
  unconfirmedPickups: Object.freeze(['2026-12-24', '2026-12-31'])
});

const pacificClock = new Intl.DateTimeFormat('en-US', {
  timeZone: shippingPolicy.timeZone, calendar: 'gregory', numberingSystem: 'latn',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
});
const dateLabel = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC', weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
});
const partsAt = epoch => Object.fromEntries(pacificClock.formatToParts(epoch)
  .filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
const dayKey = parts => `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
const nextDay = key => new Date(Date.parse(`${key}T12:00:00Z`) + 86400000).toISOString().slice(0, 10);
const knownDay = key => key >= shippingPolicy.verifiedFrom && key <= shippingPolicy.verifiedThrough;
const serviceDay = key => ![0, 6].includes(new Date(`${key}T12:00:00Z`).getUTCDay()) && !shippingPolicy.closed.includes(key);
// Merchant dispatch is Tuesday-Friday; carrier transit/delivery still includes Monday.
const dispatchDay = key => serviceDay(key) && shippingPolicy.dispatchWeekdays.includes(new Date(`${key}T12:00:00Z`).getUTCDay());

// Find the UTC instant for 3pm on a Pacific calendar date. Recalculate the offset
// for that date, so crossing either DST boundary never adds a fixed 24-hour day.
export function cutoffFor(key) {
  const target = Date.parse(`${key}T15:00:00Z`);
  let candidate = target;
  for (let attempt = 0; attempt < 3; attempt++) {
    const p = partsAt(candidate);
    const represented = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    candidate += target - represented;
  }
  return candidate;
}

export function shippingService(amount) {
  if (!Number.isFinite(amount) || amount <= 0 || amount === 2000) return null;
  return amount > 2000
    ? {name: 'UPS Next Day Air', days: 1}
    : {name: 'UPS 2nd Day Air', days: 2};
}

export function estimateDelivery(now, {amount, available = true, additionalCalendarDays = 0}) {
  if (!available) return {status: 'availability'};
  const service = shippingService(amount);
  if (!service || !Number.isFinite(now)) return {status: 'confirmation'};
  let dispatch = dayKey(partsAt(now));
  if (!knownDay(dispatch)) return {status: 'calendar'};
  if (now >= cutoffFor(dispatch)) dispatch = nextDay(dispatch);
  for (let searched = 0; searched < 14; searched++, dispatch = nextDay(dispatch)) {
    if (!knownDay(dispatch)) return {status: 'calendar'};
    if (!dispatchDay(dispatch)) continue;
    // These dates permit air delivery, but pickup requires a merchant arrangement.
    // Do not promise a later dispatch either: show a confirmation fallback instead.
    if (shippingPolicy.unconfirmedPickups.includes(dispatch)) return {status: 'pickup'};
    let arrival = dispatch;
    for (let remaining = service.days; remaining > 0;) {
      arrival = nextDay(arrival);
      if (!knownDay(arrival)) return {status: 'calendar'};
      if (serviceDay(arrival)) remaining--;
    }
    if (additionalCalendarDays === 7) {
      arrival = new Date(Date.parse(`${arrival}T12:00:00Z`) + 7 * 86400000).toISOString().slice(0, 10);
    }
    return {status: 'estimated', dispatch, arrival, cutoff: cutoffFor(dispatch), service: service.name};
  }
  return {status: 'confirmation'};
}

export function deliveryText(estimate, now) {
  if (estimate.status !== 'estimated') return estimate.status === 'availability'
    ? "This item SOLD but we may have one available Let's Find!"
    : 'Please contact the store to confirm your estimated delivery date.';
  const seconds = Math.max(0, Math.ceil((estimate.cutoff - now) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const remainder = seconds % 60;
  const duration = `${hours} ${hours === 1 ? 'hour' : 'hours'} ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ${remainder} ${remainder === 1 ? 'second' : 'seconds'}`;
  return `Order in the next ${duration} for estimated delivery ${dateLabel.format(new Date(`${estimate.arrival}T12:00:00Z`))}.`;
}

// Each tick recomputes from the current clock, including the arrival date. Returning
// to a suspended tab updates immediately; elapsed timers cannot retain stale dates.
export function mountDelivery(element, options, runtime) {
  const update = () => {
    const now = runtime.now();
    const estimate = estimateDelivery(now, options);
    if (estimate.status === 'availability' && element.dataset.deliverySearchUrl) {
      if (!element.querySelector('[data-delivery-search]')) {
        const link = element.ownerDocument.createElement('a');
        link.dataset.deliverySearch = '';
        link.style.textDecoration = 'underline';
        link.style.textUnderlineOffset = '3px';
        link.href = element.dataset.deliverySearchUrl;
        link.textContent = element.dataset.deliveryFindLabel;
        element.textContent = element.dataset.deliverySoldMessage + ' ';
        element.insertAdjacentHTML('beforeend', '<svg data-delivery-search-icon viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" style="display:inline-block;vertical-align:-2px;margin-right:2px"><circle cx="10.5" cy="10.5" r="6.5"></circle><path d="m16 16 5 5"></path></svg>');
        element.append(link);
      }
      return;
    }
    element.textContent = deliveryText(estimate, now).replace(' for estimated delivery ', '\nfor estimated delivery ');
  };
  update();
  const timer = runtime.setInterval(update, 1000);
  runtime.addVisibilityListener(update);
  return () => {
    runtime.clearInterval(timer);
    runtime.removeVisibilityListener(update);
  };
}

if (typeof document !== 'undefined') {
  const element = document.querySelector('[data-delivery-estimate]');
  if (element) {
    const baseAdditionalDays = element.dataset.deliveryBaseAdditionalCalendarDays ?? element.dataset.deliveryAdditionalCalendarDays;
    let untrackedVariants = {};
    try {
      untrackedVariants = JSON.parse(document.getElementById('preview-delivery-inventory')?.textContent || '{}');
    } catch {}
    let unmount;
    const mount = (amount, available) => {
      unmount?.();
      unmount = mountDelivery(element, {amount, available, additionalCalendarDays: element.dataset.deliveryAdditionalCalendarDays === '7' ? 7 : 0}, {
        now: () => Date.now(), setInterval: (fn, ms) => window.setInterval(fn, ms),
        clearInterval: id => window.clearInterval(id),
        addVisibilityListener: fn => document.addEventListener('visibilitychange', fn),
        removeVisibilityListener: fn => document.removeEventListener('visibilitychange', fn)
      });
    };
    mount(Number(element.dataset.deliveryPrice), element.dataset.deliveryAvailable === 'true');
    document.addEventListener('preview:variant-change', event => {
      const variant=event.detail;
      if (Object.prototype.hasOwnProperty.call(untrackedVariants, String(variant.id))) {
        element.dataset.deliveryAdditionalCalendarDays = baseAdditionalDays === '7' || untrackedVariants[String(variant.id)] === true ? '7' : '0';
      }
      element.dataset.deliveryPrice=String(variant.price/100);
      element.dataset.deliveryAvailable=String(variant.available);
      const heading=element.parentElement.querySelector('strong');
      heading.textContent=variant.price>200000?'FREE Shipping via UPS Next Day Air ✈':variant.price<200000?'FREE Shipping via UPS 2nd Day Air ✈':'Contact us to confirm shipping';
      mount(variant.price/100,variant.available);
    });
  }
}




